<template>
  <div v-if="isVisible" class="psp-overlay" @click.self="close" role="dialog" aria-modal="true" aria-labelledby="psp-title" @keydown.esc="close">

    <div class="psp-dialog">

      <!-- ═══ HEADER ═══ -->
      <div class="psp-head">
        <div class="psp-head-left">
          <div class="psp-head-icon"><i class="ri-price-tag-3-line"></i></div>
          <div>
            <div id="psp-title" class="psp-head-title">Sélectionnez le prix de vente</div>
            <div class="psp-head-sub">
              {{ dataPrices.length }} option{{ dataPrices.length > 1 ? 's' : '' }} disponible{{ dataPrices.length > 1 ? 's' : '' }}
            </div>
          </div>
        </div>
        <button class="psp-close" @click="close" aria-label="Fermer la modale">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <!-- ═══ BANDEAU ARTICLE ═══ -->
      <div v-if="currentItem && currentItem.name" class="psp-banner">
        <div class="psp-banner-icon"><i class="ri-shopping-bag-line"></i></div>
        <div>
          <div class="psp-banner-label">Article sélectionné</div>
          <div class="psp-banner-name">{{ currentItem.name }}</div>
        </div>
      </div>

      <!-- ═══ RECHERCHE (si > 8 prix) ═══ -->
      <div v-if="dataPrices.length > 8" class="psp-search-bar">
        <div class="psp-search-wrap">
          <i class="ri-search-line psp-search-icon"></i>
          <input
            v-model="searchQuery"
            type="search"
            class="psp-search"
            placeholder="Rechercher un prix..."
            @keyup.enter="selectFirstResult"
          />
          <button v-if="searchQuery" @click="searchQuery = ''" class="psp-search-clear" aria-label="Effacer">
            <i class="ri-close-line"></i>
          </button>
        </div>
      </div>

      <!-- ═══ BODY (scrollable) ═══ -->
      <div class="psp-body">

        <template v-if="filteredPrices.length">
          <button
            v-for="(price, index) in filteredPrices"
            :key="index"
            @dblclick="selectPrice(price)"
            @click="selectedIndex = index"
            :class="['psp-item', { 'psp-item-active': selectedIndex === index }]"
          >
            <div :class="['psp-item-num', { 'psp-item-num-active': selectedIndex === index }]">
              {{ index + 1 }}
            </div>
            <div class="psp-item-info">
              <div class="psp-item-price-val">{{ formatPrice(price) }}</div>
              <div v-if="currentItem && currentItem.price" class="psp-item-diff">
                Écart : {{ formatPriceDifference(price) }}
              </div>
            </div>
            <i v-if="selectedIndex === index" class="ri-check-line psp-item-check"></i>
            <i v-else class="ri-arrow-right-s-line psp-item-arrow"></i>
          </button>
        </template>

        <!-- Empty state -->
        <div v-else class="psp-empty">
          <i class="ri-price-tag-line psp-empty-icon"></i>
          <p>{{ searchQuery ? 'Aucun résultat' : 'Aucun prix disponible' }}</p>
          <small v-if="searchQuery">Essayez de modifier votre recherche</small>
        </div>

      </div>

      <!-- ═══ FOOTER ═══ -->
      <div class="psp-foot">
        <div v-if="currentItem && currentItem.price" class="psp-foot-info">
          <span class="psp-foot-label">Prix actuel :</span>
          <span class="psp-foot-val">{{ formatPrice(currentItem.price) }}</span>
        </div>
        <div class="psp-foot-actions">
          <button class="psp-btn-sec" @click="close">Annuler</button>
          <button
            v-if="selectedIndex >= 0"
            class="psp-btn-pri"
            @click="selectPrice(filteredPrices[selectedIndex])"
          >
            <i class="ri-check-line"></i> Sélectionner
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
    import { currencyFormatDE } from '@/allfunction';

    export default {
        /* eslint-disable */
        name: 'PriceSelectionModal',

        emits: ['selectPrice', 'closed'],

        data() {
            return {
                // Visibility
                isVisible: false,

                // Data
                dataPrices: [],
                currentItem: null,
                accompagnement: null,

                // Search & Selection
                searchQuery: '',
                selectedIndex: -1,
            };
        },

        computed: {
            filteredPrices() {
                if (!this.searchQuery.trim()) {
                    return this.dataPrices;
                }

                const query = this.searchQuery.toLowerCase().trim();
                return this.dataPrices.filter(price => {
                    const priceStr = this.formatPrice(price).toLowerCase();
                    return priceStr.includes(query);
                });
            },
        },

        watch: {
            searchQuery() {
                this.selectedIndex = -1;
            },
        },

        methods: {
            // ============ MODAL METHODS ============
            show(dataPrices, currentItem, accompagnement = null) {
                this.dataPrices = Array.isArray(dataPrices) ? dataPrices : [];
                this.currentItem = currentItem || null;
                this.accompagnement = accompagnement || null;
                this.searchQuery = '';
                this.selectedIndex = -1;
                this.isVisible = true;

                document.body.style.overflow = 'hidden';
                document.addEventListener('keydown', this.handleKeydown);

                // Auto-select first item if only one
                if (this.dataPrices.length === 1) {
                    this.selectedIndex = 0;
                }
            },

            close() {
                this.isVisible = false;
                document.body.style.overflow = '';
                document.removeEventListener('keydown', this.handleKeydown);
                this.$emit('closed');
            },

            // ============ SELECTION METHODS ============
            selectPrice(price) {
                if (!price) return;

                const selectedData = {
                    price: price,
                    item: this.currentItem,
                    accompagnement: this.accompagnement,
                    originalPrice: this.currentItem?.price || null,
                    priceDifference: this.currentItem?.price ? price - this.currentItem.price : null,
                };

                this.$emit('chekprice', selectedData);
                this.close();
            },

            selectFirstResult() {
                if (this.filteredPrices.length > 0) {
                    const index = this.selectedIndex >= 0 ? this.selectedIndex : 0;
                    this.selectPrice(this.filteredPrices[index]);
                }
            },

            // ============ KEYBOARD NAVIGATION ============
            handleKeydown(event) {
                if (!this.isVisible) return;

                switch (event.key) {
                    case 'Escape':
                        this.close();
                        break;
                    case 'ArrowDown':
                        event.preventDefault();
                        this.selectedIndex = Math.min(
                            this.selectedIndex + 1,
                            this.filteredPrices.length - 1
                        );
                        this.scrollToSelected();
                        break;
                    case 'ArrowUp':
                        event.preventDefault();
                        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
                        this.scrollToSelected();
                        break;
                    case 'Enter':
                        if (this.selectedIndex >= 0 && this.filteredPrices[this.selectedIndex]) {
                            this.selectPrice(this.filteredPrices[this.selectedIndex]);
                        } else if (this.filteredPrices.length === 1) {
                            this.selectPrice(this.filteredPrices[0]);
                        }
                        break;
                    case 'Home':
                        event.preventDefault();
                        this.selectedIndex = 0;
                        this.scrollToSelected();
                        break;
                    case 'End':
                        event.preventDefault();
                        this.selectedIndex = this.filteredPrices.length - 1;
                        this.scrollToSelected();
                        break;
                }
            },

            scrollToSelected() {
                this.$nextTick(() => {
                    const selected = this.$el?.querySelector('.psp-item-active');
                    selected?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                });
            },

            // ============ UTILITIES ============
            formatPrice(value) {
                if (value === null || value === undefined) return 'N/A';
                return currencyFormatDE(value);
            },

            formatPriceDifference(newPrice) {
                if (!this.currentItem?.price) return '';
                const difference = newPrice - this.currentItem.price;
                const sign = difference >= 0 ? '+' : '';
                return `${sign}${currencyFormatDE(difference)}`;
            },
        },

        beforeUnmount() {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', this.handleKeydown);
        },
    };
</script>

<style scoped>
/* ── Overlay ── */
.psp-overlay {
  position: fixed; inset: 0; z-index: 10030;
  background: rgba(0, 0, 0, .55);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  font-family: 'Segoe UI', system-ui, sans-serif;
}

/* ── Dialog ── */
.psp-dialog {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 14px; width: min(420px, 100%);
  max-height: 90dvh; display: flex; flex-direction: column;
  overflow: hidden; box-shadow: 0 24px 60px rgba(0, 0, 0, .45);
  animation: psp-in .22s cubic-bezier(.4, 0, .2, 1);
}
@keyframes psp-in {
  from { opacity: 0; transform: scale(.94) translateY(10px); }
  to   { opacity: 1; transform: none; }
}

/* ── Header ── */
.psp-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.psp-head-left { display: flex; align-items: center; gap: 10px; }
.psp-head-icon {
  width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  background: color-mix(in srgb, var(--blue) 15%, transparent);
  color: var(--blue); font-size: 18px;
  display: flex; align-items: center; justify-content: center;
}
.psp-head-title { font-size: 14px; font-weight: 800; color: var(--text); }
.psp-head-sub   { font-size: 11px; color: var(--text2); margin-top: 1px; }
.psp-close {
  background: none; border: none; color: var(--text2); font-size: 20px;
  cursor: pointer; line-height: 1; padding: 4px; border-radius: 6px;
  flex-shrink: 0; transition: color .12s;
}
.psp-close:hover { color: var(--red); }

/* ── Bandeau article ── */
.psp-banner {
  display: flex; align-items: center; gap: 10px;
  margin: 12px 14px 4px; padding: 10px 12px;
  background: color-mix(in srgb, var(--blue) 8%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--blue) 30%, var(--border));
  border-radius: 10px; flex-shrink: 0;
}
.psp-banner-icon {
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  background: color-mix(in srgb, var(--blue) 20%, transparent);
  color: var(--blue); font-size: 15px;
  display: flex; align-items: center; justify-content: center;
}
.psp-banner-label {
  font-size: 10px; font-weight: 700; color: var(--blue);
  text-transform: uppercase; letter-spacing: .4px;
}
.psp-banner-name { font-size: 13px; font-weight: 700; color: var(--text); margin-top: 1px; }

/* ── Search bar ── */
.psp-search-bar { padding: 10px 14px 4px; flex-shrink: 0; }
.psp-search-wrap { position: relative; }
.psp-search-icon {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  color: var(--text3); pointer-events: none; font-size: 15px;
}
.psp-search {
  width: 100%; padding: 9px 34px 9px 32px;
  background: var(--surface2); border: 1px solid var(--border);
  border-radius: 8px; color: var(--text); font-size: 13px;
  outline: none; font-family: inherit; transition: border-color .15s;
}
.psp-search:focus { border-color: var(--blue); }
.psp-search::placeholder { color: var(--text3); }
.psp-search-clear {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: var(--text2); cursor: pointer;
  font-size: 14px; padding: 0;
}

/* ── Body scrollable ── */
.psp-body {
  flex: 1; overflow-y: auto; padding: 12px 14px;
  display: flex; flex-direction: column; gap: 6px;
  scrollbar-width: thin; scrollbar-color: var(--border) transparent;
}
.psp-body::-webkit-scrollbar { width: 4px; }
.psp-body::-webkit-scrollbar-track { background: transparent; }
.psp-body::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }

/* ── Items ── */
.psp-item {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 12px; border-radius: 10px;
  background: var(--surface2); border: 1px solid var(--border);
  cursor: pointer; text-align: left; width: 100%; font-family: inherit;
  color: var(--text); transition: border-color .1s, background .1s;
}
.psp-item:hover {
  border-color: var(--blue);
  background: color-mix(in srgb, var(--blue) 6%, var(--surface2));
}
.psp-item-active {
  border-color: var(--blue) !important;
  background: color-mix(in srgb, var(--blue) 12%, var(--surface2)) !important;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--blue) 30%, transparent);
}
.psp-item-num {
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  background: var(--border); color: var(--text2);
  font-size: 12px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  transition: background .1s, color .1s;
}
.psp-item:hover .psp-item-num {
  background: color-mix(in srgb, var(--blue) 20%, transparent);
  color: var(--blue);
}
.psp-item-num-active { background: var(--blue) !important; color: #fff !important; }
.psp-item-info { flex: 1; min-width: 0; }
.psp-item-price-val { font-size: 18px; font-weight: 800; color: var(--text); }
.psp-item-active .psp-item-price-val { color: var(--blue); }
.psp-item-diff { font-size: 11px; color: var(--text2); margin-top: 2px; }
.psp-item-check { font-size: 20px; color: var(--blue); flex-shrink: 0; font-weight: 800; }
.psp-item-arrow { font-size: 16px; color: var(--text3); flex-shrink: 0; transition: color .1s; }
.psp-item:hover .psp-item-arrow { color: var(--blue); }

/* ── Empty state ── */
.psp-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 48px 20px; text-align: center;
}
.psp-empty-icon { font-size: 38px; color: var(--text3); margin-bottom: 12px; }
.psp-empty p    { font-size: 14px; font-weight: 600; color: var(--text2); }
.psp-empty small { font-size: 11px; color: var(--text3); margin-top: 4px; display: block; }

/* ── Footer ── */
.psp-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 12px 14px; border-top: 1px solid var(--border); flex-shrink: 0;
  background: color-mix(in srgb, var(--surface2) 50%, var(--surface));
}
.psp-foot-info { font-size: 12px; color: var(--text2); }
.psp-foot-label { font-weight: 600; }
.psp-foot-val { font-weight: 800; color: var(--text); margin-left: 6px; }
.psp-foot-actions { display: flex; gap: 8px; margin-left: auto; }
.psp-btn-sec {
  padding: 8px 16px; border-radius: 8px;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text2); font-size: 13px; font-weight: 600;
  cursor: pointer; font-family: inherit; transition: all .12s;
}
.psp-btn-sec:hover { border-color: var(--red); color: var(--red); }
.psp-btn-pri {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 18px; border-radius: 8px; border: none;
  background: var(--blue); color: #fff; font-size: 13px; font-weight: 700;
  cursor: pointer; font-family: inherit; transition: opacity .12s;
}
.psp-btn-pri:hover { opacity: .88; }

/* ── Mobile bottom-sheet ── */
@media (max-width: 640px) {
  .psp-overlay { align-items: flex-end; padding: 0; }
  .psp-dialog {
    width: 100%; max-height: 92dvh;
    border-radius: 16px 16px 0 0;
    animation: psp-slide-up .25s cubic-bezier(.4, 0, .2, 1);
  }
  .psp-foot-info { display: none; }
}
@keyframes psp-slide-up {
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
</style>
